import { CheckIcon, PencilIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'

import { formatDateTime, getFeedback, getSzansa } from './rozmowaDetailsHelpers'

export default function RozmowaNoteSection({
	rozmowa,
	editing,
	noteValue,
	nextConversationValue,
	salesOpportunityValue,
	saving,
	saveError,
	onCancel,
	onSave,
	onNoteChange,
	onNextConversationChange,
	onSalesOpportunityChange,
	onStartEdit,
}) {
	const { t } = useTranslation()
	const isSalesOpportunity = getSzansa(rozmowa)

	return (
		<div className="mb-5 rounded-md border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<h2 className="text-md font-semibold text-gray-900">{t('conversations.details.sections.editable')}</h2>
				{!editing && onStartEdit && (
					<button
						type="button"
						onClick={onStartEdit}
						className="inline-flex items-center gap-1 rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100">
						<PencilIcon className="h-4 w-4" />
						<span>{t('conversations.details.actions.editNote')}</span>
					</button>
				)}
				{editing && (
					<div className="flex flex-wrap items-center gap-2">
						<button
							type="button"
							onClick={onCancel}
							disabled={saving}
							className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60">
							<XMarkIcon className="h-4 w-4" />
							<span>{t('conversations.details.note.cancel')}</span>
						</button>
						<button
							type="button"
							onClick={onSave}
							disabled={saving}
							className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-400">
							<CheckIcon className="h-4 w-4" />
							<span>{saving ? t('conversations.details.note.saving') : t('conversations.details.note.save')}</span>
						</button>
					</div>
				)}
			</div>

			{editing ? (
				<div className="mt-4 space-y-4">
					<label className="flex flex-col gap-1">
						<span className="text-sm font-medium text-gray-700">{t('conversations.details.sections.note')}</span>
						<textarea
							value={noteValue}
							onChange={event => onNoteChange(event.target.value)}
							disabled={saving}
							rows={8}
							maxLength={5000}
							placeholder={t('conversations.details.note.placeholder')}
							className="block w-full resize-y rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className="text-sm font-medium text-gray-700">{t('conversations.details.fields.nextConversation')} *</span>
						<input
							type="date"
							value={nextConversationValue}
							onChange={event => onNextConversationChange(event.target.value)}
							disabled={saving}
							className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
						/>
					</label>
					<label className="inline-flex items-center gap-2">
						<input
							type="checkbox"
							checked={salesOpportunityValue}
							onChange={event => onSalesOpportunityChange(event.target.checked)}
							disabled={saving}
							className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
						/>
						<span className="text-sm font-medium text-gray-700">
							{t('conversations.details.fields.salesOpportunity')}
						</span>
					</label>
					{saveError && <p className="text-sm text-red-600">{saveError}</p>}
				</div>
			) : (
				<dl className="mt-4 space-y-4 text-sm">
					<div>
						<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('conversations.details.sections.note')}</dt>
						<dd className="mt-0.5 whitespace-pre-wrap leading-relaxed text-gray-700">{getFeedback(rozmowa)}</dd>
					</div>
					<div>
						<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('conversations.details.fields.nextConversation')}</dt>
						<dd className="mt-0.5 text-gray-900">{formatDateTime(rozmowa?.dig_nastepnej_rozmowy)}</dd>
					</div>
					<div>
						<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('conversations.details.fields.salesOpportunity')}</dt>
						<dd className="mt-0.5 text-gray-900">
							{isSalesOpportunity ? t('conversations.list.filters.salesOpportunityYes') : t('conversations.list.filters.salesOpportunityNo')}
						</dd>
					</div>
				</dl>
			)}
		</div>
	)
}
